import { DiagnosticsStatusByClassReportQuery$data } from '../../graphQLOperations/__generated__/DiagnosticsStatusByClassReportQuery.graphql';

export type ClassDiagnosticStatusReport = DiagnosticsStatusByClassReportQuery$data['classDiagnosticStatusReport'];
export type StudentReport = ClassDiagnosticStatusReport[number];

// "Closer look" thresholds. NOTE: totalActiveTime comes from the query in SECONDS,
// while the design expresses these limits in minutes.
export const TOO_LONG_SECONDS = 70 * 60; // >= 70 min → "taking too long"
export const CRITICAL_SECONDS = 90 * 60; // > 90 min → "critical" (vs. "mild" 70–90 min)

/** Builds a display name from the query's split first/last name fields. */
export const getStudentName = (student: StudentReport['student']): string =>
  [student.firstName, student.lastName].filter(Boolean).join(' ');

/** Last name used to sort the "closer look" lists alphabetically; missing names sort first. */
export const getStudentLastName = (student: StudentReport['student']): string => student.lastName ?? '';

/** Formats an active-time duration given in SECONDS into a compact "1h 5m" / "42m" label. */
export const formatActiveTime = (seconds: number): string => {
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
};

/**
 * Severity split for the "taking too long" list. Callers pass students who already cleared
 * TOO_LONG_SECONDS, so "mild" is simply everything that isn't critical.
 */
export const isCriticalStudent = (student: StudentReport): boolean => (student.totalActiveTime ?? 0) > CRITICAL_SECONDS;

/**
 * The "taking too long" students, ordered alphabetically by last name.
 *
 * Callers pass students already narrowed to "in progress" — each screen resolves that status
 * with its own helper — so this only applies the time threshold and the ordering, keeping the
 * cut-off identical everywhere it is shown.
 *
 * A missing measurement is "unknown", not "0 minutes", so those students are left out rather
 * than being treated as having spent no time at all.
 */
export const selectTakingTooLongStudents = (students: StudentReport[]): StudentReport[] =>
  students
    .filter(student => student.totalActiveTime != null && student.totalActiveTime >= TOO_LONG_SECONDS)
    .sort((a, b) => getStudentLastName(a.student).localeCompare(getStudentLastName(b.student)));
